import React from 'react';
import { IconBox, IconStar } from '../../assets/svg/icons';
import ScButton from '../../components/atoms/ScButton';
import { ButtonKind } from '../../components/atoms/Button/enums';
import useLanguageContext from '../../hooks/useLanguageContext';
import SupportedLanguages from '../../setup/intl/enums';
import { Brand, Product } from './types';
import { StyledBrandTile, StyledStarRow, StyledStarValue } from './styles2';
import { StyledCountPill, StyledImageFallback } from './styles';

// Brand abbreviation tile (SPEC: BrandTile), colored from the brand hue.
export const BrandTile: React.FC<{
  brand: Pick<Brand, 'name' | 'hue'>;
  size?: number;
  radius?: string;
}> = ({ brand, size, radius }) => (
  <StyledBrandTile $hue={brand.hue} $size={size} $radius={radius}>
    {brand.name.slice(0, 2)}
  </StyledBrandTile>
);

// Gold star + bold rating value (SPEC: StarRow).
export const StarRow: React.FC<{ r: number; size?: number }> = ({ r, size }) => (
  <StyledStarRow $size={size} className="tnum">
    <IconStar />
    <StyledStarValue>{r}</StyledStarValue>
  </StyledStarRow>
);

// Ink Cart button with lime count pill (SPEC: CartButton).
export const CartButton: React.FC<{ count: number; onClick: () => void; label: string }> = ({
  count,
  onClick,
  label,
}) => (
  <ScButton kind={ButtonKind.Ink} icon={IconBox} onClick={onClick}>
    {label}
    {count > 0 && <StyledCountPill className="tnum">{count}</StyledCountPill>}
  </ScButton>
);

// Real product image when one exists, else an honest branded fallback (brand
// monogram + product glyph on the brand's hue) — never a random stock photo.
// `glyphScale` lets thumbnails shrink the fallback to match their size.
export const ProductImage: React.FC<{ product: Product; glyphScale?: number }> = ({
  product,
  glyphScale,
}) => {
  const { language } = useLanguageContext();
  const src = product.images?.[0];
  if (src) {
    const name =
      language === SupportedLanguages.Arabic
        ? product.nameAr || product.name
        : product.name;
    const alt = product.brand ? `${name} — ${product.brand}` : name;
    return <img src={src} alt={alt} loading="lazy" />;
  }
  return (
    <StyledImageFallback $hue={product.hue} $scale={glyphScale} aria-hidden="true">
      <span className="mono">{(product.brand || product.name).slice(0, 2).toUpperCase()}</span>
      <IconBox />
    </StyledImageFallback>
  );
};
